// src/App.jsx
import React, { useContext, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import PublicRoute from "./components/PublicRoute";
import PrivateRoute from "./components/PrivateRoute";
import { AuthContext } from "./context/AuthContext";
import { getToken, getRole } from "./services/authStorage";
import { departmentDashboardPath, normalizeRole } from "./utils/roles";

// Auth & Onboarding Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import OnboardingContact from "./pages/Onboarding/OnboardingContact";
import OnboardingVerify from "./pages/Onboarding/OnboardingVerify";
import OnboardingDetails from "./pages/Onboarding/OnboardingDetails";
import OnboardingUpload from "./pages/Onboarding/OnboardingUpload";
import OnboardingSubmit from "./pages/Onboarding/OnboardingSubmit";
import ResetPassword from "./pages/ResetPassword";

// Dashboard Pages
import Dashboard from "./pages/Dashboard";
import Portfolio from "./pages/Portfolio";
import Performance from "./pages/Performance";
import Wallet from "./pages/Wallet";
import Transactions from "./pages/Transactions";
import Settings from "./pages/Settings";

// Admin
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminInvestments from "./pages/admin/AdminInvestments";
import AdminInvestmentSubscriptions from "./pages/admin/AdminInvestmentSubscriptions";
import AdminInvestmentSubscriptionDetail from "./pages/admin/AdminInvestmentSubscriptionDetail";
import AdminInvestmentCashflows from "./pages/admin/AdminInvestmentCashflows";
import AdminClient360 from "./pages/admin/AdminClient360";
import GeneralLedger from "./pages/admin/GeneralLedger";
import PendingTransactions from "./pages/admin/PendingTransactions";
import AdminReports from "./pages/admin/AdminReports";
import ComplianceApprovals from "./pages/admin/ComplianceApprovals";
import ComplianceManualCreate from "./pages/admin/ComplianceManualCreate";
import ComplianceStaffCreate from "./pages/admin/ComplianceStaffCreate";
import CompliancePreview from "./pages/admin/CompliancePreview";
import ComplianceCRM from "./pages/admin/ComplianceCRM";
import ComplianceCRMDetail from "./pages/admin/ComplianceCRMDetail";
import BackofficePostings from "./pages/admin/BackofficePostings";
import ChartOfAccounts from "./pages/admin/BankAccounts";
import FixedIncomeWizard from "./components/fixedIncome/FixedIncomeWizard";
import FirmInvestments from "./pages/admin/FirmInvestments";
import FirmInvestmentDetail from "./pages/admin/FirmInvestmentDetail";
import FirmReports from "./pages/admin/FirmReports";
import DepartmentDashboard from "./pages/admin/DepartmentDashboard";

// Misc
import UnauthorizedPage from "./pages/UnauthorizedPage";
import NotFoundPage from "./pages/NotFoundPage";
import TestFileUpload from "./components/TestFileUpload";

const App = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const { user } = useContext(AuthContext);

  const publicRoutes = [
    "/login",
    "/register",
    "/onboarding/contact",
    "/onboarding/verify",
    "/onboarding/details",
    "/onboarding/upload",
    "/onboarding/submit",
    "/reset-password",
  ];
  const isPublicPath = publicRoutes.includes(location.pathname);

  const RootRedirect = () => {
    const activeToken = getToken();
    const role = normalizeRole(user?.role || getRole());
    if (!activeToken) return <Navigate to="/login" replace />;
    return <Navigate to={departmentDashboardPath(role)} replace />;
  };

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900">
      {!isPublicPath && (
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      )}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!isPublicPath && (
          <Header onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />
        )}
        <main className="flex-1 overflow-y-auto px-4 py-6 md:px-8 bg-slate-50">
          <Routes>
            <Route path="/" element={<RootRedirect />} />

            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />
            <Route
              path="/onboarding/contact"
              element={
                <PublicRoute>
                  <OnboardingContact />
                </PublicRoute>
              }
            />
            <Route
              path="/onboarding/verify"
              element={
                <PublicRoute>
                  <OnboardingVerify />
                </PublicRoute>
              }
            />
            <Route
              path="/onboarding/details"
              element={
                <PublicRoute>
                  <OnboardingDetails />
                </PublicRoute>
              }
            />
            <Route
              path="/onboarding/upload"
              element={
                <PublicRoute>
                  <OnboardingUpload />
                </PublicRoute>
              }
            />
            <Route
              path="/onboarding/submit"
              element={
                <PublicRoute>
                  <OnboardingSubmit />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute allowedRoles={["user"]}>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/portfolio"
              element={
                <PrivateRoute allowedRoles={["user"]}>
                  <Portfolio />
                </PrivateRoute>
              }
            />
            <Route
              path="/performance"
              element={
                <PrivateRoute allowedRoles={["user"]}>
                  <Performance />
                </PrivateRoute>
              }
            />
            <Route
              path="/wallet"
              element={
                <PrivateRoute allowedRoles={["user"]}>
                  <Wallet />
                </PrivateRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <PrivateRoute allowedRoles={["user"]}>
                  <Transactions />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute allowedRoles={["user"]}>
                  <Settings />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <PrivateRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/accounts/dashboard"
              element={
                <PrivateRoute allowedRoles={["admin", "accounts"]}>
                  <DepartmentDashboard department="accounts" />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/compliance/dashboard"
              element={
                <PrivateRoute allowedRoles={["admin", "compliance"]}>
                  <DepartmentDashboard department="compliance" />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/finance/dashboard"
              element={
                <PrivateRoute allowedRoles={["admin", "finance"]}>
                  <DepartmentDashboard department="finance" />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/investments"
              element={
                <PrivateRoute allowedRoles={["admin"]}>
                  <AdminInvestments />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/investments/subscriptions"
              element={
                <PrivateRoute allowedRoles={["admin", "finance"]}>
                  <AdminInvestmentSubscriptions />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/investments/subscriptions/:investmentId"
              element={
                <PrivateRoute allowedRoles={["admin", "finance"]}>
                  <AdminInvestmentSubscriptionDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/investments/cashflows"
              element={
                <PrivateRoute allowedRoles={["admin", "accounts"]}>
                  <AdminInvestmentCashflows />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <PrivateRoute allowedRoles={["admin", "accounts"]}>
                  <AdminReports />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/backoffice/postings"
              element={
                <PrivateRoute allowedRoles={["admin", "accounts"]}>
                  <BackofficePostings />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/general-ledger"
              element={
                <PrivateRoute allowedRoles={["admin", "accounts"]}>
                  <GeneralLedger />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/general-ledger/pending"
              element={
                <PrivateRoute allowedRoles={["admin", "accounts"]}>
                  <PendingTransactions />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/finance/bank-accounts"
              element={
                <PrivateRoute allowedRoles={["admin", "accounts"]}>
                  <ChartOfAccounts />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/finance/client-360"
              element={
                <PrivateRoute allowedRoles={["admin", "accounts", "compliance", "finance"]}>
                  <AdminClient360 />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/fixed-income"
              element={
                <PrivateRoute allowedRoles={["admin", "finance"]}>
                  <FixedIncomeWizard />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/firm/investments"
              element={
                <PrivateRoute allowedRoles={["admin", "finance"]}>
                  <FirmInvestments />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/firm/investments/:investmentId"
              element={
                <PrivateRoute allowedRoles={["admin", "finance"]}>
                  <FirmInvestmentDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/firm/reports"
              element={
                <PrivateRoute allowedRoles={["admin", "finance"]}>
                  <FirmReports />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/compliance"
              element={
                <PrivateRoute allowedRoles={["admin", "compliance"]}>
                  <ComplianceApprovals />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/compliance/manual-entry"
              element={
                <PrivateRoute allowedRoles={["admin", "compliance"]}>
                  <ComplianceManualCreate />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/compliance/staff"
              element={
                <PrivateRoute allowedRoles={["admin", "compliance"]}>
                  <ComplianceStaffCreate />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/compliance/:pendingId"
              element={
                <PrivateRoute allowedRoles={["admin", "compliance"]}>
                  <CompliancePreview />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/compliance/crm"
              element={
                <PrivateRoute allowedRoles={["admin", "compliance"]}>
                  <ComplianceCRM />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/compliance/crm/:userId"
              element={
                <PrivateRoute allowedRoles={["admin", "compliance"]}>
                  <ComplianceCRMDetail />
                </PrivateRoute>
              }
            />

            <Route
              path="/test-upload"
              element={
                <PrivateRoute allowedRoles={["admin"]}>
                  <TestFileUpload />
                </PrivateRoute>
              }
            />

            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default App;
